/* NOTE:
 * This module is mapping bento change events onto card updates for backlog optimistic state updates.
 * Handling is separated onto different functions to improve documentation.
 */
import mapValues from 'lodash/mapValues';
import type { MultiActionsType } from '@atlassian/jira-bulk-operations-common/src/controllers/use-form-submit-state/types.tsx';
import {
	ChangeEventTypes,
	type ChangeEvent,
	type ChildIssueAddedEvent,
	type FieldChangedEvent,
	type FieldChangeRequestedEvent,
	type IssueRelationshipUpdatedEvent,
} from '@atlassian/jira-issue-view-model/src/change-type.tsx';
import {
	SummaryKey,
	LabelsKey,
} from '@atlassian/jira-providers-issue/src/model/issue-system-fields.tsx';
import type { Card } from '../../../../model/types.tsx';
import type { CardBulkOperationsPayloadData } from '../../../actions/cards/bulk-operations/index.tsx';

type FieldChangeEvent = FieldChangeRequestedEvent | FieldChangedEvent;

/**
 * Apply a bento summary change event onto a card.
 * We assume that fieldValue will be a string.
 */
export const updateCardWithSummaryChangeEvent = (
	oldCard: Card,
	changeEvent: FieldChangeEvent,
): Card => ({
	...oldCard,
	summary: changeEvent.meta.fieldValue || '',
});

/**
 * Apply a bento labels change event onto a card.
 * We assume that fieldValue will be an array of strings.
 */
const updateCardWithLabelsChangeEvent = (oldCard: Card, changeEvent: FieldChangeEvent): Card => ({
	...oldCard,
	labels: changeEvent.meta.fieldValue,
});

const updateCardWithParentChangeEvent = (
	oldCard: Card,
	changeEvent: IssueRelationshipUpdatedEvent,
): Card => ({
	...oldCard,
	parentId: Number(changeEvent.meta.newParentIssueId),
});

/**
 * Apply a bento change event over a backlog card. In the board this is a service/epic so that a
 * `ChangeEvent` may trigger an action in addition to simply updating a card.
 *
 * This helper is necessary regardless of whether we need to extract this into an epic later.
 *
 * There's a risk in here which is `ChangeEvent::meta::fieldValue` being typed as `any`. This means
 * we can't really rely on it.
 */
export const updateCardWithChangeEvent = (oldCard: Card, changeEvent: ChangeEvent): Card => {
	if (changeEvent.type === ChangeEventTypes.ISSUE_RELATIONSHIP_UPDATED) {
		return updateCardWithParentChangeEvent(oldCard, changeEvent);
	}

	if (
		changeEvent.type === ChangeEventTypes.FIELD_CHANGE_REQUESTED ||
		changeEvent.type === ChangeEventTypes.FIELD_CHANGED
	) {
		switch (changeEvent.meta.fieldId) {
			case SummaryKey:
				return updateCardWithSummaryChangeEvent(oldCard, changeEvent);

			case LabelsKey:
				return updateCardWithLabelsChangeEvent(oldCard, changeEvent);

			default:
				return oldCard;
		}
	}

	return oldCard;
};

export const buildCardFromEvent = (changeEvent: ChildIssueAddedEvent): [string, Card] => {
	const {
		issueId,
		meta: {
			childIssue: { id, issueKey, issueLink, issueSummary, issueTypeIconUrl, issueTypeName },
		},
	} = changeEvent;

	const card: Card = {
		id: Number(id),
		key: issueKey || '',
		statusId: '',
		issueUrl: issueLink || '',
		summary: issueSummary || '',
		icon: issueTypeIconUrl || '',
		typeId: 0,
		labels: [],
		assignee: null,
		parentId: Number(issueId),
		priority: null,
		dueDate: null,
		estimate: null,
		fixVersions: [],
		numCompleteChildren: 0,
		numTotalChildren: 0,
		isDone: false,
		isFlagged: false,
		projectId: 0,
		issueTypeName: issueTypeName || '',
	};

	return [String(id), card];
};

export const updateArrayByAction = <T,>(
	input: Array<T>,
	action: MultiActionsType,
	data: Array<T>,
) => {
	if (action === 'add') {
		const filtered = data.filter((value) => !input.includes(value));
		return [...input, ...filtered];
	}
	if (action === 'remove') {
		return input.filter((value) => !data.includes(value));
	}
	if (action === 'set') {
		return data;
	}
	return input;
};

const transformData = (cardData: Card) => (data: unknown, key: string) => {
	if (key === 'labels') {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-non-null-assertion
		const { action, value } = (data as CardBulkOperationsPayloadData['labels'])!;
		return updateArrayByAction(cardData.labels, action, value);
	}
	if (key === 'fixVersions' && cardData.fixVersions) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-non-null-assertion
		const { action, value } = (data as CardBulkOperationsPayloadData['fixVersions'])!;
		return updateArrayByAction(cardData.fixVersions, action, value);
	}
	return data;
};

export const transformBulkOperationsData =
	(data: CardBulkOperationsPayloadData) => (cardData: Card) =>
		mapValues(data, transformData(cardData));
